import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { NotificationType } from '../../enums/NotificationType';
import { WaiterCallService } from '../../services/waiter-call.service';

@Component({
  selector: 'app-table-actions',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './table-actions.html',
})
export class TableActionsComponent {
  public waiterService = inject(WaiterCallService);
  private router = inject(Router);
  private translate = inject(TranslateService);

  NotificationType = NotificationType;

  // Optional Note State
  isNoteOpen = signal<boolean>(false);
  customNote = signal<string>('');
  selectedQuickKey = signal<string | null>(null);

  // Router URL tracking for dynamic offset on product-detail page
  currentUrl = signal<string>(this.router.url);

  isProductDetail = computed(() => {
    const pathWithoutQuery = this.currentUrl().split('?')[0];
    const segments = pathWithoutQuery.split('/').filter(Boolean);
    // :customerID/:menuID/:categoryID/:productID has 4 segments
    return segments.length >= 4;
  });

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects || event.url);
      });
  }

  toggleNote() {
    this.isNoteOpen.update((v) => !v);
  }

  selectChip(key: string) {
    if (this.selectedQuickKey() === key) {
      this.selectedQuickKey.set(null);
      this.customNote.set('');
    } else {
      this.selectedQuickKey.set(key);
      const text = this.translate.instant(key);
      this.customNote.set(text);
    }
  }

  getEffectiveDescription(): string | undefined {
    const text = this.customNote().trim();
    return text.length > 0 ? text : undefined;
  }

  async onCallWaiter() {
    const desc = this.getEffectiveDescription();
    const success = await this.waiterService.callWaiter(desc);
    if (success) {
      this.resetNote();
    }
  }

  async onRequestBill() {
    const desc = this.getEffectiveDescription();
    const success = await this.waiterService.requestBill(desc);
    if (success) {
      this.resetNote();
    }
  }

  resetNote() {
    this.isNoteOpen.set(false);
    this.customNote.set('');
    this.selectedQuickKey.set(null);
  }

  dismissToast() {
    this.waiterService.dismissToast();
  }
}
